import React, { useEffect, useRef, useState } from 'react';
import {
  FiImage,
  FiMic,
  FiPhone,
  FiPlus,
  FiSend,
  FiSquare,
  FiStopCircle,
  FiX
} from 'react-icons/fi';
import { PendingMedia } from '../types';

interface ChatInputProps {
  inputMessage: string;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onSendMessage: (
    e: React.FormEvent | React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  isResponding: boolean;
  isRecording: boolean;
  recordingTime: number;
  isAudioPlaying: boolean;
  onMicClick: () => void;
  pendingMedia: PendingMedia[];
  onRemoveMedia: (id: string) => void;
  onSelectMedia: (files: FileList | null) => void;
  isMediaProcessing: boolean;
  mediaError?: string | null;
  onCallClick: () => void;
  disabled?: boolean;
  onStopResponse: () => void;
  isVoiceSupported?: boolean;
  voiceDisabledReason?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  onInputChange,
  onPaste,
  onSendMessage,
  isResponding,
  isRecording,
  recordingTime,
  isAudioPlaying,
  onMicClick,
  pendingMedia,
  onRemoveMedia,
  onSelectMedia,
  isMediaProcessing,
  mediaError = null,
  onCallClick,
  disabled = false,
  onStopResponse,
  isVoiceSupported = true,
  voiceDisabledReason = 'Voice responses are available only in English.',
}) => {
  const canSend = !disabled && (!!inputMessage.trim() || pendingMedia.length > 0);
  const [showVoiceNotice, setShowVoiceNotice] = useState(false);
  const allMediaInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!showVoiceNotice) return;
    const timer = setTimeout(() => setShowVoiceNotice(false), 2000);
    return () => clearTimeout(timer);
  }, [showVoiceNotice]);

  const openMediaPicker = () => {
    if (disabled || isResponding || isMediaProcessing) return;
    allMediaInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectMedia(e.target.files);
    e.target.value = '';
  };

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / (1024 ** exponent);
    return `${size.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  };

  return (
    <div className="chat-input-container">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isResponding && canSend) {
            onSendMessage(e);
          }
        }}
        className="input-form"
      >
        <div className="input-actions-left">
          <button
            type="button"
            className="action-btn"
            onClick={openMediaPicker}
            disabled={disabled || isResponding || isMediaProcessing}
            title={isMediaProcessing ? 'Media is processing...' : 'Upload media'}
          >
            <FiPlus />
          </button>
          <button
            type="button"
            className={`action-btn ${isRecording ? 'recording' : ''}`}
            onClick={() => {
              if (isResponding || isAudioPlaying) return;
              if (!isVoiceSupported) {
                setShowVoiceNotice(true);
                return;
              }
              onMicClick();
            }}
            title={
              !isVoiceSupported
                ? voiceDisabledReason
                : isRecording
                  ? 'Stop recording'
                  : 'Start recording'
            }
            disabled={disabled || isResponding || isAudioPlaying || isMediaProcessing}
          >
            {isRecording ? <FiSquare /> : <FiMic />}
            {isRecording && (
              <span className="recording-duration">
                {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}
              </span>
            )}
          </button>
          {showVoiceNotice && !isVoiceSupported && (
            <div className="voice-support-popup" role="status" aria-live="polite">
              Please speak in English. Other language voice is not supported currently.
            </div>
          )}
        </div>

        <div className={`input-main ${pendingMedia.length > 0 ? 'has-image' : ''}`}>
          {pendingMedia.length > 0 && (
            <div className="image-preview-container">
              <div className="image-preview-scroll">
                {pendingMedia.map((media, index) => (
                  <div key={media.id} className="image-preview-item">
                    <img src={media.preview} alt={media.name || `Image ${index + 1}`} />
                    <span className="image-preview-text">
                      <FiImage />
                      Image {index + 1}
                    </span>
                    <span className="media-size-text">{formatBytes(media.compressedSize)}</span>
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => onRemoveMedia(media.id)}
                      aria-label={`Remove ${media.name}`}
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <textarea
            value={inputMessage}
            onChange={onInputChange}
            onPaste={onPaste}
            placeholder={isRecording ? 'Recording...' : 'Ask anything...'}
            className={`message-input ${isRecording ? 'recording' : ''}`}
            rows={1}
            disabled={disabled || isRecording || isResponding || isMediaProcessing}
            onKeyDown={(e) => {
              if (isResponding) {
                e.preventDefault();
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend) {
                  onSendMessage(e);
                }
              }
            }}
          />
        </div>

        <button
          type="button"
          className={`send-button ${isResponding ? 'stop-button' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            if (isResponding) {
              onStopResponse();
            } else if (canSend) {
              onSendMessage(e);
            }
          }}
          disabled={(!canSend && !isResponding) || isRecording || isMediaProcessing}
          title={isResponding ? 'Stop generating' : 'Send message'}
        >
          {isResponding ? <FiStopCircle /> : <FiSend />}
        </button>

        <button
          type="button"
          className="call-icon-btn"
          style={{
            marginLeft: 8,
            background: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            cursor: 'pointer',
          }}
          title="Start a call"
          onClick={() => {
            if (!disabled) {
              onCallClick();
            }
          }}
          disabled={disabled || isMediaProcessing}
        >
          <FiPhone />
        </button>
      </form>

      <input
        ref={allMediaInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {mediaError && <div className="media-upload-error">{mediaError}</div>}
    </div>
  );
};

export default ChatInput;
